import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useAppSelector } from 'store/hook';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import useShiftConfig from './useShiftConfig';
import { formatShiftHour } from 'utils/helpers/customerShiftHelpers';

dayjs.extend(utc);
dayjs.extend(timezone);

export const ALWAYS_CONFIGURED_TIMEZONE_CUSTOMER = ['buz', 'myn'];

const useTimeDisplayMode = () => {
  const timezone = useAppSelector(
    (state) => state.authReducer.customer?.timezone
  );
  const customerCode = useAppSelector(
    (state) => state.persistedReducer.customer.code
  );

  const email = useAppSelector((state) => state.authReducer.email);

  const internalUserList = useAppSelector(
    (state) => state.internalUserReducer?.internalUserList
  );

  const externalUserList = useAppSelector(
    (state) => state.externalUserReducer?.externalUserList
  );

  const selectedUser = [...internalUserList, ...externalUserList].find(
    (user) => user.email === email
  ) ?? {
    userId: '',
    firstName: '',
    lastName: '',
    email: '',
    isActive: true,
    department: '',
    userCustomerAssociations: [],
    preferredTimeDisplayMode: 'configured',
  };

  const userReducerLocation = useMemo(() => {
    const isInternalUser = [...internalUserList].find(
      (user) => user.email === email
    );

    if (isInternalUser) {
      return 'internalUserReducer';
    }

    return 'externalUserReducer';
  }, [internalUserList, externalUserList, email]);

  const preferredTimeDisplayMode = ALWAYS_CONFIGURED_TIMEZONE_CUSTOMER.includes(
    customerCode
  )
    ? 'configured'
    : selectedUser.preferredTimeDisplayMode ?? 'local';

  const timezoneBasedOnDisplayMode =
    preferredTimeDisplayMode === 'local' ? dayjs.tz.guess() : timezone;

  const config = {
    preferredTimeDisplayMode,
    timezone,
    userReducerLocation,
    timezoneBasedOnDisplayMode,
  };

  return config;
};

dayjs.extend(utc);

interface ShiftHours {
  start: string;
  end: string;
}

export const useShiftHours = (
  shiftWiseAnalysis: boolean,
  shiftNumber: number
): ShiftHours | undefined => {
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const { shiftOptions } = useShiftConfig();

  const shiftHours = useMemo(() => {
    if (!shiftWiseAnalysis || !shiftNumber) {
      return undefined;
    }

    const shift = shiftOptions[shiftNumber - 1];

    if (!shift) {
      return undefined;
    }

    const todayLocal = dayjs().tz(timezone);

    return {
      start: formatShiftHour(shift.startHour, todayLocal),
      end: formatShiftHour(shift.endHour, todayLocal),
    };
  }, [
    shiftWiseAnalysis,
    shiftNumber,
    preferredTimeDisplayMode,
    timezone,
    shiftOptions,
  ]);

  return shiftHours;
};

export default useTimeDisplayMode;
